import { config } from "~/data/config";
import { FetchOptions } from "~/types";
import { RepoBase, RequestData } from "./repo_base";
import { Header, Optional } from "../../types";

// Types from brand
import {
  SimulatedTransactionResponse,
  SimulateTransactionRequest,
  BrandHowToUseStepsResponse,
  GeneratePartnerOTPResponse,
  GeneratePartnerOTPBody,
  GiftCardBalance,
  VerifyPartnerOTPResponse,
  VoucherProduct,
  BrandExtraMetadata,
  VerifyCodeResponse,
  VerifyCodeBody,
  ClientMinimalConfig,
  GenerateProcurementOTPResponse,
  OfferConstruct,
  ClientMinimalConfigOld,
  GiftCardPublicMetadataResponse,
  MultibrandPhysicalCard,
  VoucherProductTncResponse,
} from "../types/brand";

// Types from order
import {
  OrderDetails,
  PaymentOrderDetails,
  RedemptionParams,
  RedemptionResponse,
  CoinsSummaryResponse,
  SquidOrderDetailResponse,
  SquidOrderInitiateRequest,
  SquidOrderInitiateResponse,
  SquidProformaInvoiceGenerationProps,
  SquidProformaInvoiceGenerationResponse,
  SquidSimulateProps,
  SquidSimulateResponse,
  ShareableCoupon,
  VoucherDetails,
  SquidTransactionResponse,
  SquidProformaInvoicesResponse,
  SquidProformaInvoiceDetail,
  RedemptionTransactionsResponse,
  StoreOrdersResponse,
} from "../types/order";

// Types from search
import { PaginationRequest, SearchResults } from "../types/search";

// Types from auth
import {
  OtpCommunicationMedium,
  SDKAuthData,
  SDKInitParams,
  ShippingAddressResponse,
  SquidOnboarding,
  VerificationPlatform,
  VerifySquidOnboardingResponse,
} from "../types/auth";

// Types from gift
import {
  GetGiftBoxV3Response,
  GiftAuthMethod,
  GiftBoxDeliveryResponse,
  GiftBoxSendOtpResponse as GiftSendOtpResponse,
  GiftDetailsMinimalResponse,
  GiftVoucherDetailsResponse,
  VerifyOtpResponseV3 as GiftVerifyOtpResponseV3,
  PlutoGiftDetails,
  GiftBoxMetadata,
  GiftBoxCurationResponse,
  GiftBoxMetadataDetails,
  GiftDetail,
} from "../types/gift";

// Types from redeem_code
import { RedeemCodeResponse } from "../types/redeem_code";

// Mock responses
import {
  getMockGetGiftVoucherDetailsResponse,
  getMockGiftDetailsMinimalResponse,
} from "./mock_response";

// Types from user
import { UserProfile } from "../types/user";

// Types from pluto
import {
  ChatDetails,
  Credits,
  GiftCardDetails,
  GiftCardOccasion,
  GiftCardSelection,
  Gifts,
  InitiatePayment,
  PlutoConfig,
} from "../types/pluto";

// Types from transaction
import { CoinsTransactionsResponse } from "~/data/transaction";
import { CategoryResult } from "../types/category";
import { ClientConfig } from "../types/client";
import { V4HomeResponse } from "~/data/v4_homepage";
import {
  CoinPayoutSimulateResponse,
  CompleteCoinPayoutBody,
  GetPayoutInstrumentsResponse,
  GetPayoutsResponse,
  InitiateCoinPayoutBody,
  InitiateCoinPayoutResponse,
  PayoutInstrumentType,
  SDKInitBankDetailsParams,
  ValidatePayoutInstrumentBody,
  ValidatePayoutInstrumentResponse,
  EazyErpAccountDetailsParams,
  EazyErpAccountDetailsResponse,
  CoinPayoutDetailedResponse,
} from "../types/payout";

// Types from tp_cpn
import {
  ChannelRewardsSummaryResponse,
  ChannelRewardsClaimRequest,
  ChannelRewardsClaimResponse,
  VerificationStatus,
} from "../types/tp_cpn";

// Types from comms
import {
  InitializeCommsBatchRequest,
  CommsBatchResponse,
  CommsBatchEntriesResponse,
  CommsBatchEntriesParams,
  UpdateCommsTemplateRequest,
  CommsTemplateResponse,
  CommsTemplatesResponse,
  UpdateCommsChannelsRequest,
  CommsChannelsResponse,
  MapCommsRecipientsRequest,
  MapCommsRecipientsResponse,
  CommsCsvUploadResponse,
  SendCommsBatchResponse,
  CommsBatchStatusResponse,
  BatchValidationResponse,
  ResetBatchRequest,
  ResetBatchResponse,
  AssetType,
  BatchBySourceResponse,
  BatchDetailsResponse,
  CommsEntryByAssetResponse,
  CommsBatchesListParams,
  CommsBatchesListResponse,
} from "../types/comms";

import { PayOrderResponse } from "~/data/products";
import { SendOTPResult, SubmitOTPResult } from "~/data/auth";
import { BannerV3Data } from "~/data/banners";

export class Repo extends RepoBase {
  constructor(fetch: <T>(request: RequestData) => Promise<T>) {
    super();
    this.fetch = fetch;
  }

  async generateHubbleOTPViaSMS(
    phoneNumber: string,
    headers?: Record<string, string>
  ): Promise<SendOTPResult> {
    const url = new URL(`${config.baseUrl}/v1/auth/otp/generate`);
    return this.post<SendOTPResult>({
      url: url.toString(),
      body: { phoneNumber, medium: "SMS" },
      headers: headers,
      options: {
        isUnauthenticated: true,
      },
    });
  }

  async verifyHubbleOTPViaSMS(
    request: {
      phoneNumber: string;
      otp: string;
      otpToken: string;
      autoFill?: boolean;
    },
    headers?: Record<string, string>
  ): Promise<SubmitOTPResult> {
    const url = `${config.baseUrl}/v1/auth/otp/verify`;
    return this.post<SubmitOTPResult>({
      url: url,
      body: request,
      headers: headers,
      options: {
        isUnauthenticated: true,
        throwAuthError: true,
      },
    });
  }

  async simulate(
    request: SimulateTransactionRequest,
    headers?: Record<string, string>
  ): Promise<SimulatedTransactionResponse> {
    const url = `${config.baseUrl}/v3/redemptions/simulate`;
    return await this.post<SimulatedTransactionResponse>({
      url: url,
      body: request,
      headers: headers,
    });
  }

  async getEazyErpAccountDetails(
    params: EazyErpAccountDetailsParams,
    headers?: Record<string, string>
  ): Promise<EazyErpAccountDetailsResponse> {
    const path = `partner/eazyerp/account-details?memberId=${params.memberId}&mobileNo=${params.mobileNo}`;
    return this.get<EazyErpAccountDetailsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async getEazyErpCromptonAccountDetails(
    params: EazyErpAccountDetailsParams,
    headers?: Record<string, string>
  ): Promise<EazyErpAccountDetailsResponse> {
    const path = `partner/eazyerp-crompton/account-details?memberId=${params.memberId}&mobileNo=${params.mobileNo}`;
    return this.get<EazyErpAccountDetailsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async getPaymentOrderDetails(
    orderId: string,
    headers?: Record<string, string>
  ): Promise<PaymentOrderDetails> {
    const path = `v1/orders/${orderId}`;
    return this.get<PaymentOrderDetails>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async getStoreOrderDetails(
    orderId: string,
    headers?: Record<string, string>
  ): Promise<OrderDetails> {
    const path = `v1/store/orders/${orderId}`;
    return this.get<OrderDetails>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async getStoreOrders(
    headers?: Record<string, string>
  ): Promise<StoreOrdersResponse> {
    const path = "v1/store/orders";
    return this.get<StoreOrdersResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async initiateRedemption(
    params: RedemptionParams,
    headers?: Record<string, string>
  ): Promise<RedemptionResponse> {
    const path = "v2/redemptions/provider/pg/initiate";
    return this.post<RedemptionResponse>({
      url: `${config.baseUrl}/${path}`,
      body: params,
      headers: headers,
    });
  }

  async payOrderStandardCheckout(
    orderId: string,
    headers?: Record<string, string>
  ): Promise<PayOrderResponse> {
    const path = `v2/orders/${orderId}/pay`;
    return this.post<PayOrderResponse>({
      url: `${config.baseUrl}/${path}`,
      body: { mode: "STANDARD_CHECKOUT", customData: {} },
      headers: headers,
    });
  }

  async verifyUser(visitorId: string, platform: VerificationPlatform) {
    const path = "v1/user/verify";
    return this.post<void>({
      url: `${config.baseUrl}/${path}`,
      body: {
        id: visitorId,
        platform: platform,
      },
    });
  }

  async getProductSearch(props: {
    query?: string;
    category?: string;
    pagination?: PaginationRequest;
    headers?: Record<string, string>;
    brandKey?: string[];
    options?: FetchOptions;
  }): Promise<SearchResults> {
    const path = "v3/store/products/search";
    const initialQuery = props.brandKey
      ? `brandKey=${props.brandKey}`
      : props.category
        ? `category=${props.category}`
        : `q=${props.query}`;
    const queryString = props.pagination
      ? `${initialQuery}&limit=${props.pagination.limit}&pageNo=${props.pagination.pageNo}`
      : `${initialQuery}`;
    return this.get<SearchResults>({
      url: `${config.baseUrl}/${path}?${queryString}`,
      headers: props.headers,
      options: props.options,
    });
  }

  async getBrandHowToUseSteps(
    brandId: string
  ): Promise<BrandHowToUseStepsResponse> {
    const path = `v1/store/products/voucher-product/${brandId}/how-to-redeem-details`;
    return this.get<BrandHowToUseStepsResponse>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getVoucherProductTnc(
    productId: string
  ): Promise<VoucherProductTncResponse> {
    const path = `v1/store/products/voucher-product/${productId}/tnc`;
    return this.get<VoucherProductTncResponse>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async toggleVoucherStatus(voucherId: string): Promise<void> {
    const path = `v1/store/voucher/${voucherId}/toggle-status`;
    return this.post<void>({
      url: `${config.baseUrl}/${path}`,
      body: null,
    });
  }

  async getGiftCardBalance(
    token: string,
    cardNumber: string,
    cardPin: string
  ): Promise<GiftCardBalance> {
    const url = `${config.baseUrl}/public/utils/card-balance`;
    return this.post<GiftCardBalance>({
      url: url,
      body: { cardNumber, cardPin, token },
    });
  }

  async getBrand(
    brandIdOrBrandKey: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<VoucherProduct> {
    const url = `${config.baseUrl}/v1/store/products/voucher-product/${brandIdOrBrandKey}`;
    return this.get<VoucherProduct>({
      url: url,
      headers: headers,
      options: options,
    });
  }

  async verifyPartnerCode(
    headers: {
      clientId: string;
      clientSecret: string;
    },
    body: VerifyCodeBody,
    options?: FetchOptions
  ): Promise<VerifyCodeResponse> {
    const { clientId, clientSecret } = headers;
    const { code } = body;

    const url = `${config.baseUrl}/v1/auth/partner/code/verify`;
    return this.post<VerifyCodeResponse>({
      url: url,
      body: {
        code,
      },
      headers: {
        [Header.ClientId]: clientId,
        [Header.ClientSecret]: clientSecret,
      },
      options: options,
    });
  }

  async generatePartnerOTP(
    headers: {
      clientId: string;
      clientSecret: string;
    },
    body: GeneratePartnerOTPBody,
    options?: FetchOptions
  ): Promise<GeneratePartnerOTPResponse> {
    const { clientId, clientSecret } = headers;
    const { phoneNumber, email, medium } = body;

    const url = `${config.baseUrl}/v1/auth/partner/otp/generate`;
    return this.post<GeneratePartnerOTPResponse>({
      url: url,
      body: {
        phoneNumber,
        email,
        medium,
      },
      headers: {
        [Header.ClientId]: clientId,
        [Header.ClientSecret]: clientSecret,
      },
      options: options,
    });
  }

  async verifyPartnerOTP(
    headers: {
      clientId: string;
      clientSecret: string;
    },
    body: GeneratePartnerOTPBody,
    options?: FetchOptions
  ): Promise<VerifyPartnerOTPResponse> {
    const { clientId, clientSecret } = headers;
    const { phoneNumber, email, otp, otpToken } = body;

    const url = `${config.baseUrl}/v1/auth/partner/otp/verify`;
    return this.post<VerifyPartnerOTPResponse>({
      url: url,
      body: {
        phoneNumber,
        email,
        otp,
        otpToken,
      },
      headers: {
        [Header.ClientId]: clientId,
        [Header.ClientSecret]: clientSecret,
      },
      options: options,
    });
  }

  async generatePartnerCodeOTP(
    headers: {
      clientId: string;
      clientSecret: string;
    },
    body: GeneratePartnerOTPBody,
    options?: FetchOptions
  ): Promise<GeneratePartnerOTPResponse> {
    const { clientId, clientSecret } = headers;
    const { phoneNumber, email, medium, code } = body;

    const url = `${config.baseUrl}/v1/auth/partner/code/otp/send`;
    return this.post<GeneratePartnerOTPResponse>({
      url: url,
      body: {
        phoneNumber,
        email,
        medium,
        code,
      },
      headers: {
        [Header.ClientId]: clientId,
        [Header.ClientSecret]: clientSecret,
      },
      options: options,
    });
  }

  async verifyPartnerCodeOTP(
    headers: {
      clientId: string;
      clientSecret: string;
    },
    body: GeneratePartnerOTPBody,
    options?: FetchOptions
  ): Promise<VerifyPartnerOTPResponse> {
    const { clientId, clientSecret } = headers;
    const { phoneNumber, email, otp, otpToken, code } = body;

    const url = `${config.baseUrl}/v1/auth/partner/code/otp/verify`;
    return this.post<VerifyPartnerOTPResponse>({
      url: url,
      body: {
        phoneNumber,
        email,
        otp,
        otpToken,
        code,
      },
      headers: {
        [Header.ClientId]: clientId,
        [Header.ClientSecret]: clientSecret,
      },
      options: options,
    });
  }

  async getCoinTransactions(
    headers?: Record<string, string>
  ): Promise<CoinsTransactionsResponse> {
    const path = `v1/coins/transactions`;
    return this.get<CoinsTransactionsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async logout() {
    const url = new URL(`${config.baseUrl}/v1/auth/logout`);
    return this.post<void>({
      url: url.toString(),
      body: {},
      options: {
        isUnauthenticated: false,
      },
    });
  }

  async sendOtpForGiftBox(params: {
    id: string;
    deviceVerificationToken?: string;
    identifier?: string;
    authMethod?: GiftAuthMethod | null;
  }): Promise<GiftSendOtpResponse> {
    if (params.id.includes("mock")) {
      console.table({
        id: params.id,
        identifier: params.identifier,
        authMethod: params.authMethod,
        endpoint: `/v2/gifting/gift-box/${params.id}/auth/send-otp`,
        deviceVerificationToken: params.deviceVerificationToken,
      });
      await new Promise((resolve) => setTimeout(resolve, 4000));
      return {
        otpToken: "otp-token",
        availableMediums: ["SMS", "WHATSAPP"],
      };
    } else {
      const path = `v2/gifting/gift-box/${params.id}/auth/send-otp`;
      return this.post<GiftSendOtpResponse>({
        url: `${config.baseUrl}/${path}`,
        body: {
          identifier: params.identifier,
          authMethod: params.authMethod || "PHONE_OTP",
        },
        headers: {
          [Header.FpDeviceToken]: params.deviceVerificationToken ?? "",
        },
      });
    }
  }

  async getBanners(): Promise<BannerV3Data> {
    const path = `v1/store/banners`;
    return this.get<BannerV3Data>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getGiftBoxBanners(key: string): Promise<BannerV3Data> {
    const path = `v1/store/banners/for-gift-box/${key}`;
    return this.get<BannerV3Data>({
      url: `${config.baseUrl}/${path}`,
      options: {
        isUnauthenticated: true,
      },
    });
  }

  async openGiftBox(params: {
    id: string;
    deviceVerificationToken?: string;
    otp?: string;
    otpToken?: string;
  }): Promise<GiftVoucherDetailsResponse> {
    if (params.id.includes("mock")) {
      return getMockGetGiftVoucherDetailsResponse(params.id);
    }

    const path = `v2/gifting/gift-box/${params.id}/open`;
    return this.post<GiftVoucherDetailsResponse>({
      url: `${config.baseUrl}/${path}`,
      body: {
        otp: params.otp,
        otpToken: params.otpToken,
      },
      headers: {
        [Header.FpDeviceToken]: params.deviceVerificationToken ?? "",
      },
    });
  }

  async retryGiftBoxWhatsappDelivery(
    id: string,
    deviceVerificationToken: string | undefined
  ): Promise<GiftBoxDeliveryResponse> {
    if (id.includes("mock")) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return {
        retryAvailable: false,
      };
    }

    const path = `v2/gifting/gift-box/${id}/retry-delivery`;
    return this.post<GiftBoxDeliveryResponse>({
      url: `${config.baseUrl}/${path}`,
      body: {},
      headers: {
        [Header.FpDeviceToken]: deviceVerificationToken ?? "",
      },
    });
  }

  async getGiftDetailsMinimalResponse(
    key: string,
    deviceVerificationToken?: string
  ): Promise<GiftDetailsMinimalResponse> {
    if (key.includes("mock")) {
      console.table({
        key,
        deviceVerificationToken,
        endpoint: `/v2/gifting/gift-box/${key}`,
      });
      return await getMockGiftDetailsMinimalResponse(key);
    }
    const url = `${config.baseUrl}/v2/gifting/gift-box/${key}`;
    return this.get<GiftDetailsMinimalResponse>({
      url: url,
      headers: {
        [Header.FpDeviceToken]: deviceVerificationToken ?? "",
      },
    });
  }

  async applyGiftCard(
    cardNumber: string,
    clientId?: string
  ): Promise<RedeemCodeResponse> {
    const path = `v1/coins/gift-card/apply`;

    const headers: Record<string, string> = {};
    if (clientId) {
      headers[Header.ClientId] = clientId;
    }

    return this.post<RedeemCodeResponse>({
      url: `${config.baseUrl}/${path}`,
      body: { cardNumber: cardNumber },
      headers: headers,
    });
  }

  async getUserProfile(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<UserProfile> {
    const url = `${config.baseUrl}/v1/user/profile`;
    return this.get<UserProfile>({
      url: url,
      headers: headers,
      options: options,
    });
  }

  async getCoinsSummary(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CoinsSummaryResponse> {
    const path = "v1/coins/summary";
    return this.get<CoinsSummaryResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getGiftBoxMetadata(
    key: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<GiftBoxMetadata> {
    const path = `v3/gifting/gift/${key}/metadata`;
    const response = await this.get<GiftBoxMetadata>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });

    // Fallback values for config if not provided by backend
    if (!response.config) {
      response.config = {
        voucherRedemptionEnabled: true,
        payoutEnabled: false,
      };
    }

    return response;
  }

  async getGiftBoxWithCurationId(
    curationId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<GiftBoxCurationResponse> {
    const path = `v1/gifting/curation/${curationId}`;

    return this.get({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getGiftBoxV3(
    key: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<GetGiftBoxV3Response> {
    const path = `v3/gifting/gift/${key}`;
    return this.get<GetGiftBoxV3Response>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async sendGiftOtpV3(params: {
    id: string;
    identifier?: string;
    authMethod?: GiftAuthMethod;
    medium?: OtpCommunicationMedium;
    headers?: Record<string, string>;
  }): Promise<GiftSendOtpResponse> {
    const path = `v3/gifting/gift/${params.id}/auth/send-otp`;
    return this.post<GiftSendOtpResponse>({
      url: `${config.baseUrl}/${path}`,
      body: {
        identifier: params.identifier,
        authMethod: params.authMethod ?? "PHONE_OTP",
        medium: params.medium ?? "SMS",
      },
      headers: params.headers,
    });
  }

  async verifyGiftOtpV3(params: {
    id: string;
    otp: string;
    otpToken: string;
    headers?: Record<string, string>;
  }): Promise<GiftVerifyOtpResponseV3> {
    const path = `v3/gifting/gift/${params.id}/auth/verify-otp`;
    return this.post<GiftVerifyOtpResponseV3>({
      url: `${config.baseUrl}/${path}`,
      body: {
        otp: params.otp,
        otpToken: params.otpToken,
      },
      headers: params.headers,
    });
  }

  async getGiftMetaData(key: string): Promise<GiftBoxMetadata> {
    const path = `v3/gifting/gift/${key}/metadata`;
    return this.get<GiftBoxMetadata>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async simulateCart(
    payload: SquidSimulateProps
  ): Promise<SquidSimulateResponse> {
    const path = "v2/procurement/orders/simulate";
    return this.post<SquidSimulateResponse>({
      url: `${config.baseUrl}/${path}`,
      body: payload,
    });
  }

  async initiateSquidOrder(
    props: SquidOrderInitiateRequest
  ): Promise<SquidOrderInitiateResponse> {
    const path = "v2/procurement/orders/initiate";
    return this.post<SquidOrderInitiateResponse>({
      url: `${config.baseUrl}/${path}`,
      body: props,
    });
  }

  async getSquidOrderDetail(props: {
    orderId: string;
  }): Promise<SquidOrderDetailResponse> {
    const path = "v2/procurement/orders/" + props.orderId;
    return this.get<SquidOrderDetailResponse>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async activateSquidOrder(props: {
    orderId: string;
  }): Promise<{ status: number }> {
    const path = `v2/procurement/orders/${props.orderId}/physical-cards/activate`;
    return this.post<{ status: number }>({
      url: `${config.baseUrl}/${path}`,
      body: {},
      responseTypeNonJson: true,
    });
  }

  async getSquidWalletBalance(options?: FetchOptions): Promise<{
    balance: number;
  }> {
    const path = "v1/procurement/wallet/balance";
    return this.get<{ balance: number }>({
      url: `${config.baseUrl}/${path}`,
      options: options,
    });
  }

  async downloadSquidOrder(orderId: string): Promise<any> {
    const path = `v1/procurement/orders/${orderId}/download/excel`;
    const headers: Record<string, string> = {};
    return this.get<any>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      responseTypeNonJson: true,
    });
  }

  async getBrandExtraMetadata(
    brandIdOrBrandKey: string,
    headers?: Record<string, string>
  ): Promise<BrandExtraMetadata> {
    const url = `${config.baseUrl}/v1/store/products/voucher-product/${brandIdOrBrandKey}/extra-metadata`;
    return this.get<BrandExtraMetadata>({
      url: url,
      headers: headers,
    });
  }

  async updatePartnerUserProfile(
    body: {
      firstName?: string;
      lastName?: string;
      email?: Optional<string>;
      phoneNumber?: Optional<string>;
    },
    headers?: Record<string, string>
  ): Promise<any> {
    const url = `${config.baseUrl}/v1/user/partner/profile`;
    return this.patch<any>({
      url: url,
      body: body,
      headers: headers,
      responseTypeNonJson: true,
    });
  }

  async getMinimalClientConfigByPortal(
    host: string
  ): Promise<ClientMinimalConfig> {
    const url = `${config.baseUrl}/v1/client/config/minimal/by-portal-url/${host}`;
    return this.get<ClientMinimalConfig>({
      url,
      options: {
        isUnauthenticated: true,
      },
    });
  }

  async createConversation(options?: FetchOptions): Promise<ChatDetails> {
    const url = `${config.baseUrl}/v1/pluto/conversation`;
    return this.post<ChatDetails>({
      url: url,
      body: {},
      options: options,
    });
  }

  async sendNewMessage(
    id: string,
    message: string,
    options?: FetchOptions
  ): Promise<ChatDetails> {
    const url = `${config.baseUrl}/v1/pluto/conversation/${id}/chat`;
    return this.post<ChatDetails>({
      url: url,
      body: { message },
      options: options,
    });
  }

  async getGift(id: string): Promise<GiftCardDetails> {
    const url = `${config.baseUrl}/v1/pluto/conversation/${id}/gift`;
    return this.get<GiftCardDetails>({
      url: url,
    });
  }

  async getPlutoCredits(options?: FetchOptions): Promise<Credits> {
    const url = `${config.baseUrl}/v1/pluto/credits`;
    return this.get<Credits>({
      url: url,
      options: options,
    });
  }

  async updateGiftOccasion(
    id: string,
    { occasionTitle, recipientName, wishMessage }: GiftCardOccasion
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/occasion`;
    return this.put<{ status: number }>({
      url: url,
      body: {
        occasionTitle,
        recipientName,
        wishMessage,
      },
    });
  }

  async updateGiftSelection(
    id: string,
    selection: GiftCardSelection
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/selection`;
    return this.put<{ status: number }>({
      url: url,
      body: selection,
    });
  }

  async updateGiftBudget(
    id: string,
    budget: number
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/budget`;
    return this.put<{ status: number }>({
      url: url,
      body: { budget },
    });
  }

  async initiatePayment(id: string): Promise<InitiatePayment> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/pay`;
    return this.post<InitiatePayment>({
      url: url,
      body: {},
    });
  }

  async getGiftById(
    id: string,
    options?: FetchOptions
  ): Promise<GiftCardDetails> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}`;
    return this.get<GiftCardDetails>({
      url: url,
      options: options,
    });
  }

  async getAllConversations(options?: FetchOptions): Promise<ChatDetails[]> {
    const url = `${config.baseUrl}/v1/pluto/conversation`;
    return this.get<ChatDetails[]>({
      url: url,
      options: options,
    });
  }

  async getAllGifts(options?: FetchOptions): Promise<Gifts[]> {
    const url = `${config.baseUrl}/v1/pluto/gifts`;
    return this.get<Gifts[]>({
      url: url,
      options: options,
    });
  }

  async getConversationById(
    id: string,
    options?: FetchOptions
  ): Promise<ChatDetails> {
    const url = `${config.baseUrl}/v1/pluto/conversation/${id}`;
    return this.get<ChatDetails>({
      url: url,
      options: options,
    });
  }

  async submitPlutoFeedback(
    id: string,
    rating: number,
    comment: string
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/rate`;
    return this.post<{ status: number }>({
      url: url,
      body: { rating, comment },
      responseTypeNonJson: true,
    });
  }

  async getShareableGiftLink(id: string): Promise<{ link: string }> {
    const url = `${config.baseUrl}/v1/pluto/gifts/${id}/share`;
    return this.get<{ link: string }>({
      url: url,
    });
  }

  async updateUserProfile(
    body: {
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      email?: string;
    },
    headers?: Record<string, string>
  ): Promise<any> {
    const url = `${config.baseUrl}/v1/user/profile`;
    return this.patch<any>({
      url: url,
      body: body,
      headers: headers,
    });
  }

  async squidOnboarding(params: {
    emailId: string;
    name: string;
    phoneNumber: string;
    businessName: string;
    businessDetails?: {
      gstNumber?: string;
      address?: {
        address1?: string;
        address2?: string;
        place?: string;
        state?: string;
        pincode?: string;
        country?: string;
      };
      gstCertificateUrl?: string;
    };
  }): Promise<SquidOnboarding> {
    const url = `${config.baseUrl}/v1/squid/onboard`;
    return this.post<SquidOnboarding>({
      url: url,
      body: params,
    });
  }

  async verifySquidOnboarding(props: {
    id: string;
    params: {
      emailId: string;
      emailVerificationToken: string;
      emailOtp: string;
      phoneNumber: string;
      phoneVerificationToken: string;
      phoneOtp: string;
    };
  }): Promise<VerifySquidOnboardingResponse> {
    const url = `${config.baseUrl}/v1/squid/${props.id}/verify`;
    return this.post<VerifySquidOnboardingResponse>({
      url: url,
      body: props.params,
    });
  }

  async getSignedUrl(params: {
    contentType: string;
  }): Promise<{ url: string }> {
    const url = `${config.baseUrl}/v1/upload/signed-url`;
    return this.post<{ url: string }>({
      url: url,
      body: {
        purpose: "BUSINESS_DOCUMENT",
        contentType: params.contentType,
      },
    });
  }

  async getProcurementProfileData(): Promise<any> {
    const path = "v1/squid";
    return this.get<any>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getProcurementPhysicalCardsToBeActivatedCount(): Promise<any> {
    const path = "v2/procurement/orders/physical-cards/to-be-activated-count";
    return this.get<any>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async generateProformaInvoice(
    params: SquidProformaInvoiceGenerationProps
  ): Promise<SquidProformaInvoiceGenerationResponse> {
    const path = "v2/procurement/proforma-invoice/generate";
    return this.post<SquidProformaInvoiceGenerationResponse>({
      url: `${config.baseUrl}/${path}`,
      body: params,
    });
  }

  async sendPIOverEmail(props: { piID: string }) {
    const path = "v2/procurement/proforma-invoice/" + props.piID + "/send";
    return this.post<void>({
      url: `${config.baseUrl}/${path}`,
      body: {},
    });
  }

  async sendInvoiceOverEmail(props: { purchaseId: string }) {
    const path = "v2/procurement/invoice/" + props.purchaseId + "/send";
    return this.post<void>({
      url: `${config.baseUrl}/${path}`,
      body: {},
    });
  }

  async generateProcurementOtp(props: {
    phone?: string;
    email?: string;
  }): Promise<GenerateProcurementOTPResponse> {
    const path = "v1/auth/squid/otp/generate";
    return this.post<GenerateProcurementOTPResponse>({
      url: `${config.baseUrl}/${path}`,
      body: props,
    });
  }

  async getPlutoGiftPublic(giftId: string): Promise<PlutoGiftDetails> {
    const url = `${config.baseUrl}/public/pluto/gifts/${giftId}`;
    return this.get<PlutoGiftDetails>({
      url: url,
    });
  }

  async getPartnerCampaignDetail(props: { transactionId: string }) {
    const url = `${config.baseUrl}/partner/ketto/campaign-details?redemptionTransactionId=${props.transactionId}`;
    return this.get<any>({
      url: url,
    });
  }

  async updateSmartGCBrandSelection(
    id: string,
    brandKeys: string[]
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}/brands`;
    return this.put<{ status: number }>({
      url: url,
      body: {
        brandKeys: brandKeys,
      },
    });
  }

  async updateSmartGCBudget(
    id: string,
    budget: number
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}/budget`;
    return this.put<{ status: number }>({
      url: url,
      body: { budget },
    });
  }

  async updateSmartGCSenderName(
    id: string,
    name: string
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}/sender-details`;
    return this.put<{ status: number }>({
      url: url,
      body: { name },
    });
  }

  async updateSmartGCWishMessage(
    id: string,
    wishMessage: string
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}/wish-message`;
    return this.put<{ status: number }>({
      url: url,
      body: { wishMessage },
    });
  }

  async initiateSmartGCPayment(id: string): Promise<InitiatePayment> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}/pay`;
    return this.post<InitiatePayment>({
      url: url,
      body: {},
    });
  }

  async getUserConfig(): Promise<Record<string, any>> {
    const url = `${config.baseUrl}/v1/user/config`;
    return this.get<Record<string, any>>({
      url: url,
    });
  }

  async updateUserConfig(body: Record<string, any>) {
    const url = `${config.baseUrl}/v1/user/config`;
    return this.patch<void>({
      url: url,
      body: body,
    });
  }

  async getShareableCoupon(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<ShareableCoupon | null> {
    const url = `${config.baseUrl}/v1/coupons/hubble-promotion`;
    return this.get<ShareableCoupon | null>({
      url: url,
      headers: headers,
      options: options,
    });
  }

  async updateBusinessAddress(addressId: string) {
    const url = `${config.baseUrl}/v1/squid/details/default-address/${addressId}`;
    return this.post<void>({
      url: url,
      body: {},
    });
  }

  async getOfferConstruct(
    headers?: Record<string, string>
  ): Promise<OfferConstruct> {
    const path = "v1/gift-card/offer-construct";
    return this.get<OfferConstruct>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
    });
  }

  async getLastNVouchers(
    n?: number,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<VoucherDetails[]> {
    let url = `${config.baseUrl}/v1/store/vouchers`;
    if (n) url += `?lastFew=${n}`;
    return this.get<VoucherDetails[]>({
      url,
      headers: headers,
      options: options,
    });
  }

  async getCategories(): Promise<CategoryResult> {
    const path = "v1/store/categories";
    return this.get<CategoryResult>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getSmartGCCurationDetails(id: string): Promise<GiftBoxMetadataDetails> {
    const url = `${config.baseUrl}/v1/smart-greeting-card/curation/${id}`;
    return this.get<GiftBoxMetadataDetails>({
      url: url,
    });
  }

  async getSquidTransactions(): Promise<SquidTransactionResponse> {
    const path = "v2/procurement/orders";
    return this.get<SquidTransactionResponse>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getClientConfig(clientId: string): Promise<ClientConfig> {
    const url = `${config.baseUrl}/v1/client/config`;

    return await this.get<ClientConfig>({
      url,
      headers: {
        [Header.ClientId]: clientId,
      },
    });
  }

  async getMinimalClientConfig(
    clientId: string
  ): Promise<ClientMinimalConfigOld> {
    const url = `${config.baseUrl}/v1/client/config/minimal`;
    return this.get<ClientMinimalConfigOld>({
      url,
      headers: {
        [Header.ClientId]: clientId,
      },
    });
  }

  async procurementSimulate(
    params: SquidSimulateProps
  ): Promise<SquidSimulateResponse> {
    const path = "v2/procurement/orders/simulate";
    return this.post<SquidSimulateResponse>({
      url: `${config.baseUrl}/${path}`,
      body: params,
    });
  }

  async getPlutoConfig(options?: FetchOptions): Promise<PlutoConfig> {
    const url = `${config.baseUrl}/v1/pluto/config`;
    return this.get<PlutoConfig>({
      url: url,
      options: options,
    });
  }

  async getGiftByConversationId(
    id: string,
    options?: FetchOptions
  ): Promise<GiftCardDetails> {
    const url = `${config.baseUrl}/v1/pluto/conversation/${id}/gift`;
    return this.get<GiftCardDetails>({
      url: url,
      options: options,
    });
  }

  async getProformaInvoices(): Promise<SquidProformaInvoicesResponse> {
    const path = "v2/procurement/proforma-invoice";
    return this.get<SquidProformaInvoicesResponse>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async getProformaInvoiceDetail(
    proformaInvoiceID: string
  ): Promise<SquidProformaInvoiceDetail> {
    const path = "public/proforma-invoice/" + proformaInvoiceID;
    return this.get<SquidProformaInvoiceDetail>({
      url: `${config.baseUrl}/${path}`,
    });
  }

  async fetchV4Home(): Promise<V4HomeResponse> {
    const url = `${config.baseUrl}/v4/homepage`;
    return this.get<V4HomeResponse>({
      url: url,
    });
  }

  async performSSOLogin(sdkInitParams: SDKInitParams): Promise<SDKAuthData> {
    const url = `${config.baseUrl}/v1/auth/external/sso-token-login`;

    return this.post<SDKAuthData>({
      url: url,
      headers: {
        [Header.ClientId]: sdkInitParams.clientId,
        [Header.ClientSecret]: sdkInitParams.clientSecret,
      },
      body: {
        token: sdkInitParams.token,
      },
    });
  }

  async fetchGiftDetail(giftId: string): Promise<GiftDetail> {
    const url = `${config.baseUrl}/v1/gifting/gift/${giftId}`;
    return this.get<GiftDetail>({
      url: url,
    });
  }

  async verifySDKInitBankDetails(
    body: SDKInitBankDetailsParams
  ): Promise<ValidatePayoutInstrumentResponse> {
    const path = "v1/payouts/instruments/bank/init";
    return this.post<ValidatePayoutInstrumentResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
    });
  }

  async validatePayoutInstrument(
    body: ValidatePayoutInstrumentBody,
    headers?: Record<string, string>
  ): Promise<ValidatePayoutInstrumentResponse> {
    const path = "v1/validation/payout-instrument";
    return this.post<ValidatePayoutInstrumentResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
    });
  }

  async getPayoutInstruments(
    type: PayoutInstrumentType,
    headers?: Record<string, string>
  ): Promise<GetPayoutInstrumentsResponse> {
    const url = `${config.baseUrl}/v1/payouts/instruments?type=${type}`;
    return this.get<GetPayoutInstrumentsResponse>({
      url: url,
      headers: headers,
    });
  }

  async initiateCoinPayout(
    body: InitiateCoinPayoutBody,
    headers?: Record<string, string>
  ): Promise<InitiateCoinPayoutResponse> {
    const url = `${config.baseUrl}/v1/coins/payouts/initiate`;
    return this.post<InitiateCoinPayoutResponse>({
      url: url,
      body: body,
      headers: headers,
    });
  }

  async completePayout({
    id,
    body,
    headers,
  }: {
    id: string;
    body: CompleteCoinPayoutBody;
    headers?: Record<string, string>;
  }): Promise<InitiateCoinPayoutResponse> {
    const url = `${config.baseUrl}/v1/coins/payouts/${id}/complete`;
    return this.post<InitiateCoinPayoutResponse>({
      url: url,
      body: body,
      headers: headers,
    });
  }

  async getCoinPayouts(
    {
      page = 0,
      size = 10,
      cursor = "",
    }: {
      page?: number;
      size?: number;
      cursor?: string;
    },
    headers?: Record<string, string>
  ): Promise<GetPayoutsResponse> {
    const url = `${config.baseUrl}/v1/coins/payouts?page=${page}&size=${size}&cursor=${cursor}`;
    return this.get<GetPayoutsResponse>({
      url: url,
      headers: headers,
    });
  }

  async getPayoutById(
    id: string,
    headers?: Record<string, string>
  ): Promise<CoinPayoutDetailedResponse> {
    const url = `${config.baseUrl}/v1/coins/payouts/${id}`;
    return this.get<CoinPayoutDetailedResponse>({
      url: url,
      headers: headers,
    });
  }

  async simulatePayout({
    amount,
  }: {
    amount: number;
  }): Promise<CoinPayoutSimulateResponse> {
    const url = `${config.baseUrl}/v1/coins/payouts/simulate`;
    return this.post<CoinPayoutSimulateResponse>({
      url: url,
      body: {
        amount,
      },
    });
  }

  async getShippingAddress(): Promise<ShippingAddressResponse[]> {
    const url = `${config.baseUrl}/v1/squid/shipping-address`;
    return this.get<ShippingAddressResponse[]>({
      url: url,
    });
  }

  async getDefaultShippingAddress(): Promise<{ id: string }> {
    const url = `${config.baseUrl}/v1/squid/shipping-address/default`;
    return this.get<{ id: string }>({
      url: url,
    });
  }

  async createShippingAddress(
    body: Omit<ShippingAddressResponse, "id">
  ): Promise<ShippingAddressResponse> {
    const url = `${config.baseUrl}/v1/squid/shipping-address`;
    return this.post<ShippingAddressResponse>({
      url: url,
      body: body,
    });
  }

  async updateShippingAddress(
    shippingAddressId: string,
    body: Omit<ShippingAddressResponse, "id">
  ): Promise<void> {
    const url = `${config.baseUrl}/v1/squid/shipping-address/${shippingAddressId} `;
    return this.patch<void>({
      url: url,
      body: body,
    });
  }

  async updateDefaultShippingAddress(
    shippingAddressId: string
  ): Promise<{ status: number }> {
    const url = `${config.baseUrl}/v1/squid/shipping-address/${shippingAddressId}/default`;
    return this.patch<{ status: number }>({
      url: url,
      body: {},
    });
  }

  // TP CPN APIs
  async getTpCpnSummary(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<ChannelRewardsSummaryResponse> {
    const path = "v1/channel-rewards/user/summary";
    return this.get<ChannelRewardsSummaryResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async submitTpCpnClaim(
    body: ChannelRewardsClaimRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<ChannelRewardsClaimResponse> {
    const path = "v1/channel-rewards/claims";
    return this.post<ChannelRewardsClaimResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async getTpCpnClaims(
    status?: VerificationStatus,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<ChannelRewardsClaimResponse[]> {
    const path = "v1/channel-rewards/claims";
    const queryString = status ? `?status=${status}` : "";
    return this.get<ChannelRewardsClaimResponse[]>({
      url: `${config.baseUrl}/${path}${queryString}`,
      headers: headers,
      options: options,
    });
  }

  async initiatePhoneUpdate(
    phoneNumber: string
  ): Promise<{ otpToken: string }> {
    const path = "v1/auth/partner/user/phone/initiate-update";
    return this.post<any>({
      url: `${config.baseUrl}/${path}`,
      body: { phoneNumber },
    });
  }

  async confirmPhoneUpdate(otp: string, otpToken: string): Promise<any> {
    const path = "v1/auth/partner/user/phone";
    return this.put<any>({
      url: `${config.baseUrl}/${path}`,
      body: { otp, otpToken },
    });
  }

  async getGiftCardMetadata(
    code: string,
    options?: FetchOptions
  ): Promise<GiftCardPublicMetadataResponse> {
    const path = `public/gift-card/${code}/metadata`;
    return this.get<GiftCardPublicMetadataResponse>({
      url: `${config.baseUrl}/${path}`,
      options: options,
    });
  }

  async getMultibrandPhysicalCards(): Promise<{
    data: MultibrandPhysicalCard[];
  }> {
    const url = `${config.baseUrl}/v1/gifting/templates`;
    return this.get<{
      data: MultibrandPhysicalCard[];
    }>({
      url: url,
    });
  }

  // Comms Distribution APIs
  async initialiseCommsBatch(
    body: InitializeCommsBatchRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchResponse> {
    const path = "v1/comms/distribution/batches/initialize";
    return this.post<CommsBatchResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async getCommsBatchEntries(
    batchId: string,
    params?: CommsBatchEntriesParams,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchEntriesResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/entries`;
    const queryString = params
      ? `?pageNo=${params.pageNo || 0}&limit=${params.limit || 20}`
      : "";
    return this.get<CommsBatchEntriesResponse>({
      url: `${config.baseUrl}/${path}${queryString}`,
      headers: headers,
      options: options,
    });
  }

  async updateCommsMessageTemplate(
    batchId: string,
    body: UpdateCommsTemplateRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplateResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/template`;
    return this.put<CommsTemplateResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async updateCommsChannels(
    batchId: string,
    body: UpdateCommsChannelsRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsChannelsResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/channels`;
    return this.put<CommsChannelsResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async getCommsChannels(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsChannelsResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/channels`;
    return this.get<CommsChannelsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async mapCommsRecipients(
    batchId: string,
    body: MapCommsRecipientsRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<MapCommsRecipientsResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/recipients`;
    return this.post<MapCommsRecipientsResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async downloadCommsCsvTemplate(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<any> {
    const path = `v1/comms/distribution/batches/${batchId}/csv-template`;
    return this.get<any>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
      responseTypeNonJson: true,
    });
  }

  async uploadCommsCsvRecipients(
    batchId: string,
    file: File,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsCsvUploadResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/recipients/csv`;
    const formData = new FormData();
    formData.append("file", file);

    return this.post<CommsCsvUploadResponse>({
      url: `${config.baseUrl}/${path}`,
      body: formData,
      headers: headers,
      options: options,
    });
  }

  async validateCommsRecipientMappings(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<BatchValidationResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/validate`;
    return this.get<BatchValidationResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getCommsMessageTemplate(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplateResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/template`;
    return this.get<CommsTemplateResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getCommsTemplates(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplatesResponse> {
    const path = `v1/comms/distribution/templates`;
    return this.get<CommsTemplatesResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async sendCommsBatch(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<SendCommsBatchResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/send`;
    return this.post<SendCommsBatchResponse>({
      url: `${config.baseUrl}/${path}`,
      body: {},
      headers: headers,
      options: options,
    });
  }

  async getCommsBatchStatus(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchStatusResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/status`;
    return this.get<CommsBatchStatusResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getBatchById(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<BatchDetailsResponse> {
    const path = `v1/comms/distribution/batches/${batchId}`;
    return this.get<BatchDetailsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async resetCommsBatch(
    batchId: string,
    body: ResetBatchRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<ResetBatchResponse> {
    const path = `v1/comms/distribution/batches/${batchId}/reset`;
    return this.post<ResetBatchResponse>({
      url: `${config.baseUrl}/${path}`,
      body: body,
      headers: headers,
      options: options,
    });
  }

  async getBatchBySource(
    sourceReferenceId: string,
    assetType: AssetType,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<BatchBySourceResponse> {
    const path = `v1/comms/distribution/batches/by-source?sourceReferenceId=${sourceReferenceId}&assetType=${assetType}`;
    return this.get<BatchBySourceResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getCommsEntryByAsset(
    assetId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsEntryByAssetResponse> {
    const path = `v1/comms/distribution/entries/by-asset/${assetId}`;
    return this.get<CommsEntryByAssetResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getCommsBatchesList(
    params?: CommsBatchesListParams,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchesListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.pageNo !== undefined)
      queryParams.append("pageNo", params.pageNo.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());

    const queryString = queryParams.toString();
    const path = `v1/comms/distribution/batches${queryString ? `?${queryString}` : ""}`;

    return this.get<CommsBatchesListResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }

  async getRedemptionTransactions(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<RedemptionTransactionsResponse> {
    const path = "v3/redemptions/transactions";
    return this.get<RedemptionTransactionsResponse>({
      url: `${config.baseUrl}/${path}`,
      headers: headers,
      options: options,
    });
  }
}
